import * as Sentry from '@sentry/vue'

/**
 * Safely converts a value to a string, handling edge cases that would cause
 * JSON.stringify to throw (circular references, BigInt values).
 */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, (_key, val) =>
      typeof val === 'bigint' ? val.toString() : val,
    )
  } catch {
    // Fallback for circular references or other serialization errors
    return String(value) || '[unserializable error]'
  }
}

interface ErrorContext {
  /** Additional tags to attach to the error for categorization */
  tags?: Record<string, string>
  /** Extra contextual data to include with the error */
  extra?: Record<string, unknown>
}

/**
 * Captures an exception and reports it to both Sentry and Grafana Faro.
 *
 * Use this function for errors that you want to track in monitoring systems
 * but have already handled gracefully in the application.
 *
 * @note Grafana Faro reporting only works within Vue setup context (components,
 *       composables, lifecycle hooks). Sentry reporting works from anywhere.
 *
 * @example
 * ```typescript
 * try {
 *   await riskyOperation()
 * } catch (error) {
 *   captureException(error, {
 *     tags: { feature: 'patient-registration' },
 *     extra: { patientId: '123', attemptNumber: 2 },
 *   })
 *   // Show user-friendly error message
 * }
 * ```
 *
 * @param error - The error to capture (Error object or any value)
 * @param context - Optional context with tags and extra data
 */
export function captureException(error: unknown, context?: ErrorContext): void {
  // Report to Sentry
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
  })

  // Report to Grafana Faro if available (only works within Vue setup context)
  const nuxtApp = tryUseNuxtApp()

  if (!nuxtApp && import.meta.dev) {
    console.warn(
      '[captureException] captureException is only available inside setup context',
    )
  }

  const faro = nuxtApp?.$grafanaFaro

  if (!faro && import.meta.dev) {
    console.warn('[captureException] Grafana Faro not available')
  }

  if (faro) {
    // Grafana Faro expects an Error object
    const errorObject =
      error instanceof Error
        ? error
        : new Error(
            typeof error === 'object' ? safeStringify(error) : String(error),
          )

    faro.api.pushError(errorObject, {
      context: { ...context?.tags, ...context?.extra } as
        | Record<string, string>
        | undefined,
    })
  }
}
